if (!customElements.get('quick-order-list')) {
  customElements.define(
    'quick-order-list',
    class QuickOrderList extends BulkAdd {
      cartUpdateUnsubscriber = undefined;
      hasPendingQuantityUpdate = false;
      constructor() {
        super();
        this.isListInsideModal = this.closest('bulk-modal');

        this.stickyHeaderElement = document.querySelector('sticky-header');
        if (this.stickyHeaderElement) {
          this.stickyHeader = {
            height: this.stickyHeaderElement.offsetHeight,
            type: `${this.stickyHeaderElement.getAttribute('data-sticky-type')}`,
          };
        }

        this.totalBar = this.getTotalBar();
        if (this.totalBar) {
          this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;

          this.handleResize = this.handleResize.bind(this);
          window.addEventListener('resize', this.handleResize);
        }

        this.querySelector('form').addEventListener('submit', (event) => event.preventDefault());
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
          // skip if cart event was triggered by this section
          if (event.source === this.id) return;

          this.toggleTableLoading(true);
          await this.refresh();
          this.toggleTableLoading(false);
        });

        this.initEventListeners();
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener('resize', this.handleResize);
      }

      handleResize() {
        this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;
        this.stickyHeader.height = this.stickyHeaderElement ? this.stickyHeaderElement.offsetHeight : 0;
      }

      initEventListeners() {
        this.querySelectorAll('.pagination__item').forEach((link) => {
          link.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopPropagation();

            const url = new URL(event.currentTarget.href);

            this.toggleTableLoading(true);
            await this.refresh(url.searchParams.get('page') || '1');
            this.scrollTop();
            this.toggleTableLoading(false);
          });
        });

        this.querySelector('.quick-order-list__contents').addEventListener(
          'keyup',
          this.handleScrollIntoView.bind(this)
        );

        this.quickOrderListTable.addEventListener('keydown', this.handleSwitchVariantOnEnter.bind(this));

        this.initVariantEventListeners();
      }

      initVariantEventListeners() {
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]'));

        this.querySelectorAll('quantity-input').forEach((qty) => {
          const debouncedOnChange = debounce(this.onChange.bind(this), BulkAdd.ASYNC_REQUEST_DELAY, true);
          qty.addEventListener('change', (event) => {
            this.hasPendingQuantityUpdate = true;
            debouncedOnChange(event);
          });
        });

        this.querySelectorAll('.quick-order-list-remove-button').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleLoading(true);
            this.startQueue(button.dataset.index, 0);
          });
        });
      }

      get currentPage() {
        return this.querySelector('.pagination-wrapper')?.dataset?.page ?? '1';
      }

      get cartVariantsForProduct() {
        return JSON.parse(this.querySelector('[data-cart-contents]')?.innerHTML || '[]');
      }

      onChange(event) {
        const inputValue = parseInt(event.target.value);
        this.cleanErrorMessageOnType(event);
        if (inputValue == 0) {
          event.target.setAttribute('value', inputValue);
          this.startQueue(event.target.dataset.index, inputValue);
        } else {
          this.validateQuantity(event);
        }
      }

      cleanErrorMessageOnType(event) {
        const handleKeydown = () => {
          event.target.setCustomValidity(' ');
          event.target.reportValidity();
          event.target.removeEventListener('keydown', handleKeydown);
        };

        event.target.addEventListener('keydown', handleKeydown);
      }

      validateInput(target) {
        const targetValue = parseInt(target.value);
        const targetMin = parseInt(target.dataset.min);
        const targetStep = parseInt(target.step);

        if (target.max) {
          return (
            targetValue == 0 ||
            (targetValue >= targetMin && targetValue <= parseInt(target.max) && targetValue % targetStep == 0)
          );
        } else {
          return targetValue == 0 || (targetValue >= targetMin && targetValue % targetStep == 0);
        }
      }

      get quickOrderListTable() {
        return this.querySelector('.quick-order-list__table');
      }

      getSectionsToRender() {
        return [
          {
            id: this.id,
            section: this.dataset.section,
            selector: `#${this.id}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '#shopify-section-cart-icon-bubble',
          },
          {
            id: `quick-order-list-live-region-text-${this.dataset.productId}`,
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      toggleTableLoading(enable) {
        this.quickOrderListTable.classList.toggle('quick-order-list__container--disabled', enable);
        this.toggleLoading(enable);
      }

      async refresh(pageNumber = null) {
        const url = this.dataset.url || window.location.pathname;

       return fetch(`${url}?section_id=${this.dataset.section}&page=${pageNumber || this.currentPage}`, {
  credentials: 'same-origin'
})
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const responseQuickOrderList = html.querySelector(`#${this.id}`);

            if (!responseQuickOrderList) {
              return;
            }

            this.innerHTML = responseQuickOrderList.innerHTML;
            this.initEventListeners();
          })
          .catch((e) => {
            console.error(e);
          });
      }

      renderSections(parsedState) {
        const { items, sections } = parsedState;

        this.getSectionsToRender().forEach(({ id, selector, section }) => {
          const sectionElement = document.getElementById(id);
          if (!sectionElement) return;

          const newSection = new DOMParser().parseFromString(sections[section], 'text/html').querySelector(selector);

          if (section === this.dataset.section) {
            if (this.queue.length > 0 || this.hasPendingQuantityUpdate) return;

            const focusedElement = document.activeElement;
            let focusTarget = focusedElement?.dataset?.target;
            if (focusTarget?.includes('remove')) {
              focusTarget = focusedElement.closest('quantity-popover')?.querySelector('[data-target*="increment-"]')
                ?.dataset.target;
            }

            const total = this.getTotalBar();
            if (total) {
              total.innerHTML = newSection.querySelector('.quick-order-list__total').innerHTML;
            }

            const table = this.quickOrderListTable;
            const newTable = newSection.querySelector('.quick-order-list__table');

            // only update variants if they are from the active page
            const shouldUpdateVariants =
              this.currentPage === (newSection.querySelector('.pagination-wrapper')?.dataset.page ?? '1');
            if (newTable && shouldUpdateVariants) {
              // table.innerHTML = newTable.innerHTML;

              const newFocusTarget = this.querySelector(`[data-target='${focusTarget}']`);
              if (newFocusTarget) {
                newFocusTarget?.focus({ preventScroll: true });
              }

              // this.initVariantEventListeners();
            }
          } else if (section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', items.length === 0);
            sectionElement.querySelector(selector).innerHTML = newSection.innerHTML;
          } else {
            sectionElement.innerHTML = newSection.innerHTML;
          }
        });
      }

      getTotalBar() {
        return this.querySelector('.quick-order-list__total');
      }

      scrollTop() {
        const { top } = this.getBoundingClientRect();

        if (this.isListInsideModal) {
          this.scrollIntoView();
        } else {
          window.scrollTo({ top: top + window.scrollY - (this.stickyHeader?.height || 0), behavior: 'instant' });
        }
      }

      scrollQuickOrderListTable(target) {
        const inputTopBorder = target.getBoundingClientRect().top;
        const inputBottomBorder = target.getBoundingClientRect().bottom;

        if (this.isListInsideModal) {
          const totalBarCrossesInput = inputBottomBorder > this.totalBar.getBoundingClientRect().top;
          const tableHeadCrossesInput =
            inputTopBorder < this.querySelector('.quick-order-list__table thead').getBoundingClientRect().bottom;

          if (totalBarCrossesInput || tableHeadCrossesInput) {
            this.scrollToCenter(target);
          }
        } else {
          const stickyHeaderBottomBorder = this.stickyHeaderElement?.getBoundingClientRect().bottom;
          const totalBarCrossesInput = inputBottomBorder > this.totalBarPosition;
          const inputOutsideOfViewPort =
            inputBottomBorder < this.querySelector('.variant-item__quantity-wrapper').offsetHeight;
          const stickyHeaderCrossesInput =
            this.stickyHeaderElement &&
            this.stickyHeader.type !== 'on-scroll-up' &&
            this.stickyHeader.height > inputTopBorder;
          const stickyHeaderScrollupCrossesInput =
            this.stickyHeaderElement &&
            this.stickyHeader.type === 'on-scroll-up' &&
            this.stickyHeader.height > inputTopBorder &&
            stickyHeaderBottomBorder > 0;

          if (
            totalBarCrossesInput ||
            inputOutsideOfViewPort ||
            stickyHeaderCrossesInput ||
            stickyHeaderScrollupCrossesInput
          ) {
            this.scrollToCenter(target);
          }
        }
      }

      scrollToCenter(target) {
        target.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }

      handleScrollIntoView(event) {
        if ((event.key === 'Tab' || event.key === 'Enter') && this.allInputsArray.length !== 1) {
          this.scrollQuickOrderListTable(event.target);
        }
      }

      handleSwitchVariantOnEnter(event) {
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;

        event.preventDefault();
        event.target.blur();

        if (!this.validateInput(event.target) || this.allInputsArray.length <= 1) return;

        const currentIndex = this.allInputsArray.indexOf(event.target);
        const offset = event.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + offset + this.allInputsArray.length) % this.allInputsArray.length;

        this.allInputsArray[nextIndex]?.select();
      }

      updateMultipleQty(items) {
        if (this.queue.length == 0) this.hasPendingQuantityUpdate = false;

        this.toggleLoading(true);
        const url = this.dataset.url || window.location.pathname;

        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map(({ section }) => section),
          sections_url: `${url}?page=${this.currentPage}`,
        });

        this.updateMessage();
        this.setErrorMessage();

        fetch(`${routes.cart_update_url}`, {
  ...fetchConfig(),
  body,
  credentials: 'same-origin'
})
          .then((response) => response.text())
         .then(async (state) => {
  const parsedState = JSON.parse(state);

  // Update totals etc. but don't trust stale cart drawer section
  this.renderSections(parsedState);

  // ============================================================
  // HAYATI 4K POD -> KIT ENFORCEMENT
  // 3 Pods = 1 Kit. Same flavour first; fallback to another
  // available Kit flavour. Kits increase/decrease with Pods.
  // ============================================================
  if (
    window.location.pathname.replace(/\/$/, '').endsWith('/products/hayati-pro-max-4k-pods-20mg') &&
    !this.hayatiAutoKitRunning
  ) {
    this.hayatiAutoKitRunning = true;

    try {
      const [podRes, kitRes, cartRes] = await Promise.all([
        fetch('/products/hayati-pro-max-4k-pods-20mg.js', { cache: 'no-store' }),
        fetch('/products/hayati-pro-max-4k-vape-kit-20mg.js', { cache: 'no-store' }),
        fetch('/cart.js', { cache: 'no-store' })
      ]);

      if (!podRes.ok || !kitRes.ok || !cartRes.ok) {
        throw new Error('Could not load Hayati product/cart information.');
      }

      const podProduct = await podRes.json();
      const kitProduct = await kitRes.json();
      const cart = await cartRes.json();

      const normalise = (value) => String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

      const podVariantsById = {};
      podProduct.variants.forEach((variant) => {
        podVariantsById[String(variant.id)] = variant;
      });

      const podVariantIds = new Set(
        podProduct.variants.map((variant) => String(variant.id))
      );
      const kitVariantIds = new Set(
        kitProduct.variants.map((variant) => String(variant.id))
      );
      const availableKits = kitProduct.variants.filter((variant) => variant.available === true);

      const podsInCart = cart.items.filter((item) =>
        podVariantIds.has(String(item.variant_id))
      );

      const requiredKits = {};

      for (const podItem of podsInCart) {
        const podVariant = podVariantsById[String(podItem.variant_id)];
        if (!podVariant || podItem.quantity <= 0) continue;

        // Exact rule for your pack structure:
        // 15 Pods -> 5 Kits, 30 -> 10, 45 -> 15, etc.
        // If 5 Pods are ever allowed, requirement rounds up to 5 Kits.
        const requiredQty = Math.ceil((podItem.quantity / 3) / 5) * 5;
        if (requiredQty <= 0) continue;

        const podFlavour = normalise(podVariant.title || podItem.variant_title);

        let selectedKit = availableKits.find((variant) =>
          normalise(variant.title) === podFlavour
        );

        if (!selectedKit) selectedKit = availableKits[0];

        if (!selectedKit) {
          throw new Error('No Hayati 4K Kit flavours are currently available.');
        }

        const kitId = String(selectedKit.id);
        requiredKits[kitId] = (requiredKits[kitId] || 0) + requiredQty;
      }

      // Current Hayati Kit quantities in cart.
      const currentKits = {};
      cart.items.forEach((item) => {
        if (kitVariantIds.has(String(item.variant_id))) {
          currentKits[String(item.variant_id)] =
            (currentKits[String(item.variant_id)] || 0) + item.quantity;
        }
      });

      // First reduce/remove Kits when Pods were reduced/removed.
      const reductions = {};
      Object.entries(currentKits).forEach(([kitId, currentQty]) => {
        const requiredQty = requiredKits[kitId] || 0;
        if (currentQty > requiredQty) reductions[kitId] = requiredQty;
      });

      if (Object.keys(reductions).length > 0) {
        const reduceRes = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: reductions })
        });
        const reduceData = await reduceRes.json();
        if (!reduceRes.ok || reduceData.status) {
          throw new Error(reduceData.description || reduceData.message || 'Could not reduce required Kits.');
        }
      }

      // Then add only the missing Kit quantity.
      const itemsToAdd = [];
      Object.entries(requiredKits).forEach(([kitId, requiredQty]) => {
        const currentQty = currentKits[kitId] || 0;
        const missingQty = requiredQty - currentQty;
        if (missingQty > 0) {
          itemsToAdd.push({ id: Number(kitId), quantity: missingQty });
        }
      });

      if (itemsToAdd.length > 0) {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        });
        const addData = await addRes.json();
        if (!addRes.ok || addData.status) {
          throw new Error(addData.description || addData.message || 'Could not add required Hayati Kits.');
        }
      }

      // Show the purchase rule once above the quick-order table.
      if (!document.querySelector('.hayati-kit-requirement')) {
        const table = this.querySelector('.quick-order-list__contents') || this;
        const warning = document.createElement('div');
        warning.className = 'hayati-kit-requirement';
        warning.innerHTML = `
          <strong>⚠️ Hayati 4K Pod Purchase Requirement</strong>
          <div>Hayati 4K Pods must be purchased with Hayati 4K Kits at a 3:1 ratio. Every 10 Pods requires 5 Kits. Required Kits are automatically added and adjusted with your Pod quantity.</div>
        `;
        warning.style.cssText = 'margin:0 0 16px;padding:14px 16px;border:1px solid #d6a400;border-radius:8px;background:#fff8df;line-height:1.45;';
        table.parentNode.insertBefore(warning, table);
      }

    } catch (error) {
      console.error('HAYATI AUTO KIT ERROR:', error);
      this.setErrorMessage('Hayati 4K Kit requirement: ' + error.message);
    } finally {
      this.hayatiAutoKitRunning = false;
    }
  }

  // Force fresh cart drawer + cart icon from Shopify
  try {
    const [drawerResponse, iconResponse] = await Promise.all([
      fetch(`${window.location.pathname}?section_id=cart-drawer&_=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store'
      }),
      fetch(`${window.location.pathname}?section_id=cart-icon-bubble&_=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store'
      })
    ]);

    const drawerHtml = await drawerResponse.text();
    const iconHtml = await iconResponse.text();

    const parser = new DOMParser();

    const newDrawerDoc = parser.parseFromString(drawerHtml, 'text/html');
    const newDrawer = newDrawerDoc.querySelector('#CartDrawer');

    const currentDrawer = document.querySelector('#CartDrawer');

    if (newDrawer && currentDrawer) {
      currentDrawer.innerHTML = newDrawer.innerHTML;
      const cartDrawer = document.querySelector('cart-drawer');

if (cartDrawer) {
  const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');

  if (overlay) {
    overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
  }
}
    }

    const newIconDoc = parser.parseFromString(iconHtml, 'text/html');
    const newIconSection =
      newIconDoc.querySelector('#shopify-section-cart-icon-bubble');

    const currentIconSection =
      document.querySelector('#shopify-section-cart-icon-bubble');

    if (newIconSection && currentIconSection) {
      currentIconSection.innerHTML = newIconSection.innerHTML;
    }

    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      cartDrawer.classList.toggle(
        'is-empty',
        parsedState.item_count === 0
      );

      const drawerInner = cartDrawer.querySelector('.drawer__inner');

      if (drawerInner) {
        drawerInner.classList.toggle(
          'is-empty',
          parsedState.item_count === 0
        );
      }
    }

  } catch (error) {
    console.error('Cart drawer refresh failed:', error);
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: this.id,
    cartData: parsedState,
  });
})
          .catch((e) => {
            console.error(e);
            this.setErrorMessage(window.cartStrings.error);
          })
          .finally(() => {
            this.queue.length === 0 && this.toggleLoading(false);
            this.setRequestStarted(false);
          });
      }

      setErrorMessage(message = null) {
        this.errorMessageTemplate =
          this.errorMessageTemplate ??
          document.getElementById(`QuickOrderListErrorTemplate-${this.dataset.productId}`).cloneNode(true);
        const errorElements = document.querySelectorAll('.quick-order-list-error');

        errorElements.forEach((errorElement) => {
          errorElement.innerHTML = '';
          if (!message) return;
          const updatedMessageElement = this.errorMessageTemplate.cloneNode(true);
          updatedMessageElement.content.querySelector('.quick-order-list-error-message').innerText = message;
          errorElement.appendChild(updatedMessageElement.content);
        });
      }

      updateMessage(quantity = null) {
        const messages = this.querySelectorAll('.quick-order-list__message-text');
        const icons = this.querySelectorAll('.quick-order-list__message-icon');

        if (quantity === null || isNaN(quantity)) {
          messages.forEach((message) => (message.innerHTML = ''));
          icons.forEach((icon) => icon.classList.add('hidden'));
          return;
        }

        const isQuantityNegative = quantity < 0;
        const absQuantity = Math.abs(quantity);

        const textTemplate = isQuantityNegative
          ? absQuantity === 1
            ? window.quickOrderListStrings.itemRemoved
            : window.quickOrderListStrings.itemsRemoved
          : quantity === 1
          ? window.quickOrderListStrings.itemAdded
          : window.quickOrderListStrings.itemsAdded;

        messages.forEach((msg) => (msg.innerHTML = textTemplate.replace('[quantity]', absQuantity)));

        if (!isQuantityNegative) {
          icons.forEach((i) => i.classList.remove('hidden'));
        }
      }

      updateError(updatedValue, id) {
        let message = '';
        if (typeof updatedValue === 'undefined') {
          message = window.cartStrings.error;
        } else {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(id, message);
      }

      updateLiveRegions(id, message) {
        const variantItemErrorDesktop = document.getElementById(`Quick-order-list-item-error-desktop-${id}`);
        if (variantItemErrorDesktop) {
          variantItemErrorDesktop.querySelector('.variant-item__error-text').innerHTML = message;
          variantItemErrorDesktop.closest('tr').classList.remove('hidden');
        }
        if (variantItemErrorMobile)
          variantItemErrorMobile.querySelector('.variant-item__error-text').innerHTML = message;

        this.querySelector('#shopping-cart-variant-item-status').setAttribute('aria-hidden', true);

        const cartStatus = document.getElementById('quick-order-list-live-region-text');
        cartStatus.setAttribute('aria-hidden', false);

        setTimeout(() => {
          cartStatus.setAttribute('aria-hidden', true);
        }, 1000);
      }

      toggleLoading(loading, target = this) {
        target.querySelector('#shopping-cart-variant-item-status').toggleAttribute('aria-hidden', !loading);
        target
          .querySelectorAll('.variant-remove-total .loading__spinner')
          ?.forEach((spinner) => spinner.classList.toggle('hidden', !loading));
      }
    }
  );
}

if (!customElements.get('quick-order-list-remove-all-button')) {
  customElements.define(
    'quick-order-list-remove-all-button',
    class QuickOrderListRemoveAllButton extends HTMLElement {
      constructor() {
        super();
        this.quickOrderList = this.closest('quick-order-list');

        this.actions = {
          confirm: 'confirm',
          remove: 'remove',
          cancel: 'cancel',
        };

        this.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.dataset.action === this.actions.confirm) {
            this.toggleConfirmation(false, true);
          } else if (this.dataset.action === this.actions.remove) {
            const items = this.quickOrderList.cartVariantsForProduct.reduce(
              (acc, variantId) => ({ ...acc, [variantId]: 0 }),
              {}
            );

            this.quickOrderList.updateMultipleQty(items);
            this.toggleConfirmation(true, false);
          } else if (this.dataset.action === this.actions.cancel) {
            this.toggleConfirmation(true, false);
          }
        });
      }

      toggleConfirmation(showConfirmation, showInfo) {
        this.quickOrderList
          .querySelector('.quick-order-list-total__confirmation')
          .classList.toggle('hidden', showConfirmation);
        this.quickOrderList.querySelector('.quick-order-list-total__info').classList.toggle('hidden', showInfo);
      }
    }
  );
}



